/** Study-guide quiz banks for the Multivariable Nonlinear Dynamical Systems module — 20 MCQs per topic (6 Easy / 8 Medium / 6 Hard). */

// Phase Planes and Local Stability
export const ND_PHASE_QUIZ = [
  {
    prompt: "For the system $x'=f(x,y)$, $y'=g(x,y)$, a point $(x^*,y^*)$ is an equilibrium exactly when",
    options: [
      "$f(x^*,y^*)=0$ and $g(x^*,y^*)=0$",
      "$f(x^*,y^*)=g(x^*,y^*)$",
      "$f_x(x^*,y^*)=0$",
      "$x^*=y^*$",
    ],
    answer: "A",
    explanation: "An equilibrium is a point where both velocities vanish, so the constant solution $(x(t),y(t))\\equiv(x^*,y^*)$ satisfies the system.",
    id: "nd-phase-01",
    difficulty: "Easy",
  },
  {
    prompt: "On the $x$-nullcline $\\{f(x,y)=0\\}$, the vector field $(f,g)$ points",
    options: [
      "Horizontally",
      "Vertically (or is zero)",
      "Along the nullcline",
      "Towards the origin",
    ],
    answer: "B",
    explanation: "There $x'=0$, so the velocity is $(0,g)$: straight up, straight down, or zero where it meets the $y$-nullcline (an equilibrium).",
    id: "nd-phase-02",
    difficulty: "Easy",
  },
  {
    prompt: "The linearization of $x'=f(x,y)$, $y'=g(x,y)$ at an equilibrium $\\mathbf p$ uses the matrix",
    options: [
      "$\\begin{pmatrix}f_{xx}&f_{xy}\\\\g_{xy}&g_{yy}\\end{pmatrix}$",
      "$\\begin{pmatrix}f&g\\\\0&0\\end{pmatrix}$",
      "$\\begin{pmatrix}f_x&f_y\\\\g_x&g_y\\end{pmatrix}$ evaluated at $\\mathbf p$",
      "$\\nabla(f+g)$",
    ],
    answer: "C",
    explanation: "The first-order Taylor expansion at an equilibrium is $\\mathbf F(\\mathbf p+\\mathbf h)\\approx J(\\mathbf p)\\,\\mathbf h$, with $J$ the Jacobian matrix of first partial derivatives.",
    id: "nd-phase-03",
    difficulty: "Easy",
  },
  {
    prompt: "If the Jacobian at an equilibrium has $\\det J<0$, the equilibrium is a",
    options: [
      "Stable node",
      "Centre",
      "Stable spiral",
      "Saddle",
    ],
    answer: "D",
    explanation: "$\\det J=\\lambda_1\\lambda_2<0$ means the eigenvalues are real with opposite signs: one direction attracts and one repels. Saddles are always unstable.",
    id: "nd-phase-04",
    difficulty: "Easy",
  },
  {
    prompt: "An equilibrium whose Jacobian has eigenvalues $-1$ and $-3$ is a",
    options: [
      "Stable node",
      "Unstable node",
      "Saddle",
      "Stable spiral",
    ],
    answer: "A",
    explanation: "Both eigenvalues are real and negative, so every nearby trajectory decays to the equilibrium without spiralling.",
    id: "nd-phase-05",
    difficulty: "Easy",
  },
  {
    prompt: "An equilibrium is called hyperbolic when",
    options: [
      "$\\det J<0$",
      "No eigenvalue of $J$ has zero real part",
      "The eigenvalues are complex",
      "$\\operatorname{tr}J=0$",
    ],
    answer: "B",
    explanation: "Hyperbolic means $\\operatorname{Re}\\lambda\\neq0$ for every eigenvalue. Then the Hartman–Grobman theorem guarantees the linearization predicts the local phase portrait and stability.",
    id: "nd-phase-06",
    difficulty: "Easy",
  },
  {
    prompt: "The origin of $x'=x-y$, $y'=x+y$ is",
    options: [
      "A stable spiral",
      "A saddle",
      "An unstable spiral",
      "A centre",
    ],
    answer: "C",
    explanation: "$\\operatorname{tr}J=2$, $\\det J=2$, and $\\operatorname{tr}^2-4\\det=-4<0$: complex eigenvalues $1\\pm i$ with positive real part.",
    id: "nd-phase-07",
    difficulty: "Medium",
  },
  {
    prompt: "For the damped pendulum $x'=y$, $y'=-\\sin x-\\tfrac12y$, the equilibrium $(0,0)$ is",
    options: [
      "A saddle",
      "An unstable node",
      "A centre",
      "A stable spiral",
    ],
    answer: "D",
    explanation: "$J=\\begin{pmatrix}0&1\\\\-1&-\\tfrac12\\end{pmatrix}$: $\\operatorname{tr}=-\\tfrac12$, $\\det=1$, $\\operatorname{tr}^2-4\\det=\\tfrac14-4<0$. Hyperbolic, so the pendulum spirals down to rest.",
    id: "nd-phase-08",
    difficulty: "Medium",
  },
  {
    prompt: "For the same pendulum, the equilibrium $(\\pi,0)$ (pointing straight up) is",
    options: [
      "A saddle",
      "A stable node",
      "A centre",
      "A stable spiral",
    ],
    answer: "A",
    explanation: "$J=\\begin{pmatrix}0&1\\\\-\\cos\\pi&-\\tfrac12\\end{pmatrix}=\\begin{pmatrix}0&1\\\\1&-\\tfrac12\\end{pmatrix}$, so $\\det J=-1<0$.",
    id: "nd-phase-09",
    difficulty: "Medium",
  },
  {
    prompt: "The coexistence equilibrium of the competition model $x'=x(3-x-2y)$, $y'=y(2-x-y)$ is",
    options: [
      "$(1,1)$, a stable node",
      "$(1,1)$, a saddle",
      "$(3,0)$, a saddle",
      "$(2,1)$, a stable spiral",
    ],
    answer: "B",
    explanation: "Solving $x+2y=3$, $x+y=2$ gives $(1,1)$. There $J=\\begin{pmatrix}-1&-2\\\\-1&-1\\end{pmatrix}$ with $\\det J=1-2=-1<0$.",
    id: "nd-phase-10",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=-y+x(x^2+y^2)$, $y'=x+y(x^2+y^2)$, the linearization at the origin is a centre. The true behaviour is",
    options: [
      "A centre",
      "A stable spiral",
      "An unstable spiral",
      "A saddle",
    ],
    answer: "C",
    explanation: "In polar coordinates $r'=r^3>0$ and $\\theta'=1$, so trajectories spiral outward. The cubic terms decide stability because the centre is not hyperbolic.",
    id: "nd-phase-11",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=y$, $y'=x-x^3$, the equilibrium $(0,0)$ is",
    options: [
      "A centre",
      "A stable node",
      "An unstable spiral",
      "A saddle",
    ],
    answer: "D",
    explanation: "$J=\\begin{pmatrix}0&1\\\\1-3x^2&0\\end{pmatrix}=\\begin{pmatrix}0&1\\\\1&0\\end{pmatrix}$ at the origin, with $\\det J=-1$. (At $(\\pm1,0)$ the linearization is a centre.)",
    id: "nd-phase-12",
    difficulty: "Medium",
  },
  {
    prompt: "An equilibrium with $\\operatorname{tr}J=-4$ and $\\det J=4$ is a",
    options: [
      "Stable degenerate (repeated-eigenvalue) node",
      "Saddle",
      "Stable spiral",
      "Centre",
    ],
    answer: "A",
    explanation: "$\\operatorname{tr}^2-4\\det=0$ gives the repeated eigenvalue $\\lambda=-2<0$: the boundary case between a stable node and a stable spiral.",
    id: "nd-phase-13",
    difficulty: "Medium",
  },
  {
    prompt: "Why can two different trajectories of a $C^1$ autonomous system never cross?",
    options: [
      "Because equilibria are isolated",
      "Because of uniqueness of solutions: through each point passes exactly one solution",
      "Because the Jacobian is invertible",
      "Because nullclines are straight lines",
    ],
    answer: "B",
    explanation: "If two trajectories met at a point, two different solutions would start from the same initial condition, contradicting the existence–uniqueness theorem.",
    id: "nd-phase-14",
    difficulty: "Medium",
  },
  {
    prompt: "$x'=x^2,\\;y'=-y$ and $x'=-x^3,\\;y'=-y$ have the same linearization at the origin. This shows that",
    options: [
      "Both origins are stable",
      "Both origins are unstable",
      "With a zero eigenvalue, linearization cannot decide stability",
      "The Jacobian was computed incorrectly",
    ],
    answer: "C",
    explanation: "Both have $J=\\begin{pmatrix}0&0\\\\0&-1\\end{pmatrix}$. For $x'=x^2$ points with $x>0$ move away (unstable); for $x'=-x^3$ everything decays (asymptotically stable).",
    id: "nd-phase-15",
    difficulty: "Hard",
  },
  {
    prompt: "For $x'=y$, $y'=-x+\\mu y$, the origin is an unstable spiral exactly when",
    options: [
      "$\\mu>2$",
      "$-2<\\mu<0$",
      "$\\mu<-2$",
      "$0<\\mu<2$",
    ],
    answer: "D",
    explanation: "$\\operatorname{tr}J=\\mu$, $\\det J=1$. A spiral needs $\\mu^2-4<0$, i.e. $|\\mu|<2$, and instability needs $\\mu>0$.",
    id: "nd-phase-16",
    difficulty: "Hard",
  },
  {
    prompt: "For $x'=-2x+y$, $y'=x-2y$ (eigenvalues $-1$ with eigenvector $(1,1)$ and $-3$ with $(1,-1)$), a typical trajectory approaches the origin tangent to",
    options: [
      "The direction $(1,1)$",
      "The direction $(1,-1)$",
      "The $x$-axis",
      "It spirals in",
    ],
    answer: "A",
    explanation: "The $(1,-1)$ component decays like $e^{-3t}$, much faster than the $(1,1)$ component, which decays like $e^{-t}$. So the slow direction $(1,1)$ dominates at the end.",
    id: "nd-phase-17",
    difficulty: "Hard",
  },
  {
    prompt: "In the competition model $x'=x(3-x-2y)$, $y'=y(2-x-y)$, which equilibria are asymptotically stable?",
    options: [
      "Only $(1,1)$",
      "$(3,0)$ and $(0,2)$",
      "Only $(0,0)$",
      "All four",
    ],
    answer: "B",
    explanation: "At $(3,0)$, $J=\\begin{pmatrix}-3&-6\\\\0&-1\\end{pmatrix}$; at $(0,2)$, $J=\\begin{pmatrix}-1&0\\\\-2&-2\\end{pmatrix}$: both have negative eigenvalues. $(0,0)$ is an unstable node and $(1,1)$ a saddle, so one species always dies out.",
    id: "nd-phase-18",
    difficulty: "Hard",
  },
  {
    prompt: "In polar form $r'=r(1-r^2)$, $\\theta'=1$. A trajectory starting near (but not at) the origin",
    options: [
      "Converges to the origin",
      "Escapes to infinity",
      "Spirals out and approaches the circle $r=1$",
      "Stays on a closed orbit through its starting point",
    ],
    answer: "C",
    explanation: "For $0<r<1$, $r'>0$, and $r=1$ is an equilibrium of the $r$-equation. The origin is an unstable spiral and $r=1$ is a stable limit cycle.",
    id: "nd-phase-19",
    difficulty: "Hard",
  },
  {
    prompt: "The Jacobian at an equilibrium of a nonlinear system has eigenvalues $\\pm2i$. What can you conclude?",
    options: [
      "The equilibrium is a centre",
      "The equilibrium is unstable",
      "The equilibrium is asymptotically stable",
      "Nothing yet: it may be a centre, a stable spiral or an unstable spiral",
    ],
    answer: "D",
    explanation: "Purely imaginary eigenvalues make the equilibrium non-hyperbolic, so Hartman–Grobman does not apply. Higher-order terms decide, as in the $r'=\\pm r^3$ examples.",
    id: "nd-phase-20",
    difficulty: "Hard",
  },
];

// Lyapunov Stability Methods
export const ND_LYAPUNOV_QUIZ = [
  {
    prompt: "A function $V$ is positive definite on a neighbourhood $U$ of the equilibrium $\\mathbf 0$ if",
    options: [
      "$V(\\mathbf 0)=0$ and $V(\\mathbf x)>0$ for all $\\mathbf x\\neq\\mathbf 0$ in $U$",
      "$V(\\mathbf x)\\ge0$ on $U$",
      "$V$ is increasing in every variable",
      "$\\nabla V(\\mathbf 0)\\neq\\mathbf 0$",
    ],
    answer: "A",
    explanation: "Positive definite means zero exactly at the equilibrium and strictly positive elsewhere. $V\\ge0$ alone (semi-definite) is not enough.",
    id: "nd-lyap-01",
    difficulty: "Easy",
  },
  {
    prompt: "For $\\mathbf x'=\\mathbf F(\\mathbf x)$, the derivative of $V$ along trajectories is",
    options: [
      "$\\partial V/\\partial t$",
      "$\\dot V=\\nabla V\\cdot\\mathbf F$",
      "$|\\nabla V|^2$",
      "$\\nabla\\cdot\\mathbf F$",
    ],
    answer: "B",
    explanation: "By the chain rule $\\frac{d}{dt}V(\\mathbf x(t))=\\nabla V(\\mathbf x)\\cdot\\mathbf x'(t)=\\nabla V\\cdot\\mathbf F$. It can be computed without solving the system.",
    id: "nd-lyap-02",
    difficulty: "Easy",
  },
  {
    prompt: "If $V$ is positive definite and $\\dot V$ is negative definite near $\\mathbf 0$, the equilibrium is",
    options: [
      "Unstable",
      "Stable but not necessarily attracting",
      "Locally asymptotically stable",
      "Globally asymptotically stable automatically",
    ],
    answer: "C",
    explanation: "This is Lyapunov's direct method. \"Globally\" needs more: $\\dot V<0$ on the whole space and $V$ radially unbounded.",
    id: "nd-lyap-03",
    difficulty: "Easy",
  },
  {
    prompt: "If $V$ is positive definite and only $\\dot V\\le0$ near $\\mathbf 0$, Lyapunov's theorem guarantees",
    options: [
      "Asymptotic stability",
      "Instability",
      "Nothing at all",
      "Stability (trajectories stay close), but not necessarily convergence",
    ],
    answer: "D",
    explanation: "$V$ cannot increase, so trajectories stay inside small sublevel sets. They need not approach $\\mathbf 0$ — a centre is the standard example. LaSalle's principle can sometimes upgrade this.",
    id: "nd-lyap-04",
    difficulty: "Easy",
  },
  {
    prompt: "Which function is positive definite on $\\mathbb R^2$?",
    options: [
      "$x^2+2y^2$",
      "$x^2-y^2$",
      "$x^2$",
      "$(x+y)^2$",
    ],
    answer: "A",
    explanation: "$x^2-y^2$ takes negative values, while $x^2$ and $(x+y)^2$ vanish on whole lines through the origin (only semi-definite).",
    id: "nd-lyap-05",
    difficulty: "Easy",
  },
  {
    prompt: "To conclude global asymptotic stability, in addition to $V>0$ and $\\dot V<0$ away from $\\mathbf 0$ on all of $\\mathbb R^n$, we need",
    options: [
      "$V$ to be quadratic",
      "$V$ to be radially unbounded: $V(\\mathbf x)\\to\\infty$ as $|\\mathbf x|\\to\\infty$",
      "$\\mathbf F$ to be linear",
      "$\\dot V$ to be constant",
    ],
    answer: "B",
    explanation: "Radial unboundedness makes every sublevel set $\\{V\\le c\\}$ bounded, so no trajectory can drift off to infinity while $V$ decreases.",
    id: "nd-lyap-06",
    difficulty: "Easy",
  },
  {
    prompt: "For $x'=-x^3$, $y'=-y^3$ with $V=x^2+y^2$, we get",
    options: [
      "$\\dot V=-2(x^2+y^2)$; locally stable only",
      "$\\dot V=0$; a centre",
      "$\\dot V=-2(x^4+y^4)$; globally asymptotically stable",
      "$\\dot V=2(x^4+y^4)$; unstable",
    ],
    answer: "C",
    explanation: "$\\dot V=2x(-x^3)+2y(-y^3)$ is negative definite on $\\mathbb R^2$ and $V$ is radially unbounded. The linearization (the zero matrix) could not decide this.",
    id: "nd-lyap-07",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=-x+4y$, $y'=-x-y$ with $V=x^2+4y^2$, the orbital derivative is",
    options: [
      "$-2x^2+6xy-2y^2$",
      "$-2x^2+8y^2$",
      "$0$",
      "$-2x^2-8y^2$",
    ],
    answer: "D",
    explanation: "$\\dot V=2x(-x+4y)+8y(-x-y)=-2x^2+8xy-8xy-8y^2$. The cross terms cancel, giving a negative definite $\\dot V$.",
    id: "nd-lyap-08",
    difficulty: "Medium",
  },
  {
    prompt: "For the same system, $V=x^2+y^2$ gives $\\dot V=-2x^2+6xy-2y^2$, which is positive at $x=y=1$. What follows?",
    options: [
      "Nothing; this $V$ simply does not work, so try another",
      "The origin is unstable",
      "The origin is a saddle",
      "The origin is a centre",
    ],
    answer: "A",
    explanation: "A failed candidate proves nothing. The weighted $V=x^2+4y^2$ shows the origin is asymptotically stable (also clear from $\\operatorname{tr}=-2$, $\\det=5$).",
    id: "nd-lyap-09",
    difficulty: "Medium",
  },
  {
    prompt: "For the damped pendulum $x'=y$, $y'=-\\sin x-cy$ ($c>0$) with energy $V=\\tfrac12y^2+(1-\\cos x)$, the orbital derivative is",
    options: [
      "$-c\\,y^2-\\sin x$",
      "$-c\\,y^2$",
      "$0$",
      "$-c\\,(x^2+y^2)$",
    ],
    answer: "B",
    explanation: "$\\dot V=y(-\\sin x-cy)+\\sin x\\cdot y=-cy^2\\le0$. Friction removes energy, but $\\dot V=0$ on the whole line $y=0$, so this is only semi-definite.",
    id: "nd-lyap-10",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=-x+x^3$ with $V=\\tfrac12x^2$, $\\dot V=-x^2(1-x^2)$. The best region of attraction this $V$ certifies is",
    options: [
      "All of $\\mathbb R$",
      "$(-2,2)$",
      "$(-1,1)$",
      "$\\{0\\}$ only",
    ],
    answer: "C",
    explanation: "$\\dot V<0$ for $0<|x|<1$, and $\\{V<\\tfrac12\\}=(-1,1)$ is the largest sublevel set inside that region. Indeed $x=\\pm1$ are equilibria and points beyond them blow up.",
    id: "nd-lyap-11",
    difficulty: "Medium",
  },
  {
    prompt: "Which quadratic form is positive definite?",
    options: [
      "$x^2+4xy+y^2$",
      "$x^2+2xy+y^2$",
      "$-x^2-y^2$",
      "$2x^2+2xy+y^2$",
    ],
    answer: "D",
    explanation: "Its matrix $\\begin{pmatrix}2&1\\\\1&1\\end{pmatrix}$ has leading minors $2>0$ and $\\det=1>0$. The first has $\\det=1-4<0$ (indefinite); the second is $(x+y)^2$ (semi-definite).",
    id: "nd-lyap-12",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=y$, $y'=-x-y$ with $V=\\tfrac12(x^2+y^2)$, $\\dot V=-y^2$. By LaSalle's invariance principle, trajectories converge to",
    options: [
      "The origin",
      "The whole $x$-axis",
      "A limit cycle",
      "Infinity",
    ],
    answer: "A",
    explanation: "$\\dot V=0$ on $\\{y=0\\}$. A trajectory staying there needs $y'=-x=0$, so the largest invariant subset is $\\{\\mathbf 0\\}$. With $V$ radially unbounded the stability is global.",
    id: "nd-lyap-13",
    difficulty: "Medium",
  },
  {
    prompt: "For the gradient system $\\mathbf x'=-\\nabla h(\\mathbf x)$ with a strict minimum at $\\mathbf x^*$, the choice $V=h-h(\\mathbf x^*)$ gives",
    options: [
      "$\\dot V=|\\nabla h|^2$",
      "$\\dot V=-|\\nabla h|^2$",
      "$\\dot V=0$",
      "$\\dot V=-h$",
    ],
    answer: "B",
    explanation: "$\\dot V=\\nabla h\\cdot(-\\nabla h)=-|\\nabla h|^2\\le0$: the cost never increases along the flow. This is the continuous-time reason gradient descent works.",
    id: "nd-lyap-14",
    difficulty: "Medium",
  },
  {
    prompt: "For $x'=-y+ax(x^2+y^2)$, $y'=x+ay(x^2+y^2)$ with $V=x^2+y^2$, we get $\\dot V=2a(x^2+y^2)^2$. When $a=0$ the origin is",
    options: [
      "Asymptotically stable",
      "Unstable",
      "Stable but not asymptotically stable",
      "Not an equilibrium",
    ],
    answer: "C",
    explanation: "$\\dot V\\equiv0$, so every trajectory stays on its circle $x^2+y^2=$ const: a true centre. It is stable but nothing converges.",
    id: "nd-lyap-15",
    difficulty: "Hard",
  },
  {
    prompt: "For the same system with $a>0$, the same $V$ shows that the origin is",
    options: [
      "Asymptotically stable",
      "A centre",
      "Stable",
      "Unstable, by the Lyapunov instability theorem",
    ],
    answer: "D",
    explanation: "$V(\\mathbf 0)=0$, $V>0$ arbitrarily close to $\\mathbf 0$, and $\\dot V=2ar^4$ is positive definite, so $V$ grows along every nearby trajectory and they leave any small disk.",
    id: "nd-lyap-16",
    difficulty: "Hard",
  },
  {
    prompt: "After trying several candidate functions, you fail to find a Lyapunov function for an equilibrium. You may conclude",
    options: [
      "Nothing about its stability",
      "It is unstable",
      "It is a saddle",
      "It is stable but not asymptotically stable",
    ],
    answer: "A",
    explanation: "Lyapunov's conditions are sufficient, not necessary to exhibit by hand. Failure to find $V$ is a statement about the search, not about the system.",
    id: "nd-lyap-17",
    difficulty: "Hard",
  },
  {
    prompt: "For $x'=-x+x^3$, $y'=-y$ with $V=x^2+y^2$, $\\dot V=-2x^2(1-x^2)-2y^2$. The largest disk $\\{V<c\\}$ certified to lie in the region of attraction has $c=$",
    options: [
      "$\\tfrac12$",
      "$1$",
      "$2$",
      "$\\tfrac14$",
    ],
    answer: "B",
    explanation: "$\\dot V<0$ whenever $|x|<1$ and $(x,y)\\neq\\mathbf 0$. The disk $x^2+y^2<1$ lies in that strip, but any larger disk contains points with $|x|\\ge1$, where $\\dot V$ can be $\\ge0$.",
    id: "nd-lyap-18",
    difficulty: "Hard",
  },
  {
    prompt: "Why is $V(x,y)=x^2$ not a valid Lyapunov function for an equilibrium at the origin of a planar system?",
    options: [
      "Because it is not differentiable",
      "Because $\\dot V$ is always zero",
      "Because it is not positive definite: it vanishes on the whole $y$-axis",
      "Because it is negative somewhere",
    ],
    answer: "C",
    explanation: "Points $(0,y)$ with $y\\neq0$ give $V=0$, so small sublevel sets are strips, not small regions around the origin, and they cannot trap trajectories near $\\mathbf 0$.",
    id: "nd-lyap-19",
    difficulty: "Hard",
  },
  {
    prompt: "For the linear system $\\mathbf x'=A\\mathbf x$ with $A=-I$, which $P$ solves the Lyapunov equation $A^{T}P+PA=-2I$?",
    options: [
      "$P=2I$",
      "$P=-I$",
      "$P=\\tfrac12I$",
      "$P=I$",
    ],
    answer: "D",
    explanation: "$A^{T}P+PA=-P-P=-2P$, so $-2P=-2I$ gives $P=I$, and $V=\\mathbf x^{T}P\\mathbf x=|\\mathbf x|^2$. $P=2I$ would give $-4I$.",
    id: "nd-lyap-20",
    difficulty: "Hard",
  },
];
